import { useState } from "react";
import { ChevronDown, ChevronRight, PenTool, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { StatusBadge } from "./WorkflowStatusBadge";
import { StepUsers } from "./WorkflowOverviewStepUsers";
import { UserResponseForm } from "./WorkflowOverviewStepResponseForm";
import { cn } from "~/lib/utils";
import { useUserContext } from "~/hooks/useUserContext";
import {
  type WorkflowStep,
  type WorkflowStepInstance,
  WorkflowStepUserResponses,
  WorkflowStepStatuses,
} from "~/api/types";
import { formatDate, formatDateRelative } from "~/helpers/date";
import { StepComments } from "./WorkflowOverviewStepComments";
import { Badge } from "~/atoms/badge";
import { Button } from "~/atoms/button";
import { DocumentVersionStatus } from "~/config/types";
import {
  allUsersApproved,
  getStepComments,
  getLatestStepInstance,
} from "~/helpers/workflow-instances";

type StepContentProps = {
  step: WorkflowStep;
  stepInstances: WorkflowStepInstance[];
  selectedVersionStatus: DocumentVersionStatus;
  onUserResponse: () => void;
};

export function WorkflowOverviewStep({
  step,
  stepInstances,
  selectedVersionStatus,
  onUserResponse,
}: StepContentProps) {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const [showResponseForm, setShowResponseForm] = useState(false);

  const isCompletedStep = step.status === WorkflowStepStatuses.completed;
  const [isExpanded, setIsExpanded] = useState(!isCompletedStep);

  const currentUserInStep = step.users.find((su) => su.userId === user.id);
  const isActiveStep = step.status === WorkflowStepStatuses.active;
  const stepHasUsers = step.users.length > 0;
  const allHaveApproved = allUsersApproved(step);
  const allUsersAcceptedActiveStep =
    allHaveApproved && isActiveStep && stepHasUsers;

  const hasAlreadyResponded =
    currentUserInStep &&
    currentUserInStep.responseStatus !== WorkflowStepUserResponses.pending;

  const isPublishedOrArchived =
    selectedVersionStatus === DocumentVersionStatus.published ||
    selectedVersionStatus === DocumentVersionStatus.archived;

  const canRespond =
    isActiveStep && currentUserInStep && !isPublishedOrArchived;
  const shouldShowForm = !hasAlreadyResponded || showResponseForm;

  return (
    <div
      className={cn("flex flex-col gap-2 rounded-lg border p-4", {
        "border-blue bg-blue-50/70": isActiveStep,
        "border-green bg-green-50/70":
          step.status === WorkflowStepStatuses.completed ||
          allUsersAcceptedActiveStep,
      })}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="line-clamp-2 min-w-0 font-medium wrap-break-word">
          {step.name}
        </h3>
        <div className="flex shrink-0">
          <StatusBadge status={step.status} />
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="hover:text-foreground text-muted-foreground -my-4 -mr-4 cursor-pointer p-4"
          >
            {isExpanded ? (
              <ChevronDown className="size-5" />
            ) : (
              <ChevronRight className="size-5" />
            )}
          </button>
        </div>
      </div>
      <TimeStamp step={step} stepInstances={stepInstances} />

      {isExpanded && (
        <>
          {step.type === "approval" && (
            <Badge variant="outline" className="self-start bg-white">
              <PenTool className="mr-1 h-3 w-3" />
              {t(($) => $.commons.workflow.approval)}
            </Badge>
          )}

          {step.dueAt && (
            <div className="text-xs">
              {t(($) => $.commons.workflow.responseDate)}:{" "}
              {formatDate(step.dueAt)}
            </div>
          )}

          <pre className="text-muted-foreground font-sans text-sm whitespace-pre-wrap">
            {step.description}
          </pre>

          <StepUsers users={step.users} isActive={isActiveStep} />

          {step.status !== WorkflowStepStatuses.upcoming && (
            <StepComments comments={getStepComments(stepInstances, step.id)} />
          )}

          {canRespond &&
            (shouldShowForm ? (
              <UserResponseForm
                stepId={step.id}
                instanceId={getLatestStepInstance(stepInstances, step.id)!.id}
                currentUserInStep={currentUserInStep}
                onUserResponse={async () => {
                  setShowResponseForm(false);
                  onUserResponse();
                }}
                stepType={step.type}
              />
            ) : (
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  className="self-start"
                  onClick={() => setShowResponseForm(true)}
                >
                  <Plus className="mr-1 h-3 w-3" />
                  {t(($) => $.commons.workflow.respond.leaveAnotherResponse)}
                </Button>
              </div>
            ))}
        </>
      )}
    </div>
  );
}

const TimeStamp = ({
  step,
  stepInstances,
}: {
  step: WorkflowStep;
  stepInstances: WorkflowStepInstance[];
}) => {
  const { t, i18n } = useTranslation();

  if (step.status === WorkflowStepStatuses.upcoming) return null;

  const latestInstance = getLatestStepInstance(stepInstances, step.id);
  if (!latestInstance) return null;

  switch (step.status) {
    case WorkflowStepStatuses.active:
      return (
        <div className="text-muted-foreground text-xs">
          {`${t(($) => $.commons.workflow.started)} ${formatDateRelative(latestInstance.createdAt, i18n.language)}`}
        </div>
      );
    case WorkflowStepStatuses.completed:
      if (!latestInstance.completedAt) return null;
      return (
        <div className="text-muted-foreground text-xs">
          {`${t(($) => $.commons.workflow.completed)} ${formatDateRelative(latestInstance.completedAt, i18n.language)}`}
        </div>
      );
    default:
      return null;
  }
};
